// "Не сега" history for the notification permission dialog, and when it last appeared,
// kept on the device.
// Device-level on purpose: the browser permission is per device too, and an iPhone
// Home Screen app has its own storage, separate from Safari.

export const NOTIFICATION_PROMPT_STORAGE_KEY = "planny_push_prompt_history";

/** Staff are asked on opening the platform under their own rules, so they keep their own history. */
export const STAFF_NOTIFICATION_PROMPT_STORAGE_KEY = "planny_staff_push_prompt_history";

/** The "×" on the workout rest timer's "turn notifications on" line, kept apart from the dialog's. */
export const REST_TIMER_NOTIFICATION_HINT_STORAGE_KEY = "planny_rest_timer_push_hint_history";

/**
 * When the dialog last appeared on this device, for clients and staff alike. Once seen it
 * stays away for 30 days, whatever the answer, or none (the app was closed with it open).
 */
export const NOTIFICATION_PROMPT_SHOWN_STORAGE_KEY = "planny_push_prompt_last_shown_at";

export interface INotificationPromptHistory {
  dismissCount: number;
  /** ISO timestamp of the last "Не сега", or null. */
  lastDismissedAt: string | null;
}

const emptyHistory = (): INotificationPromptHistory => ({ dismissCount: 0, lastDismissedAt: null });

const isValidHistory = (value: unknown): value is INotificationPromptHistory => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.dismissCount === "number" &&
    Number.isFinite(candidate.dismissCount) &&
    candidate.dismissCount >= 0 &&
    (candidate.lastDismissedAt === null || typeof candidate.lastDismissedAt === "string")
  );
};

export const readNotificationPromptHistory = (
  storageKey: string = NOTIFICATION_PROMPT_STORAGE_KEY
): INotificationPromptHistory => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return emptyHistory();

    const parsed: unknown = JSON.parse(raw);
    return isValidHistory(parsed)
      ? { dismissCount: parsed.dismissCount, lastDismissedAt: parsed.lastDismissedAt }
      : emptyHistory();
  } catch {
    // Unreadable data or storage blocked (private mode, cleared site data).
    return emptyHistory();
  }
};

/** Records one more "Не сега" and returns the updated history. */
export const recordNotificationPromptDismissal = (
  now: Date = new Date(),
  storageKey: string = NOTIFICATION_PROMPT_STORAGE_KEY
): INotificationPromptHistory => {
  const current = readNotificationPromptHistory(storageKey);
  const next: INotificationPromptHistory = {
    dismissCount: current.dismissCount + 1,
    lastDismissedAt: now.toISOString(),
  };

  try {
    localStorage.setItem(storageKey, JSON.stringify(next));
  } catch {
    // Storage unavailable — the dialog may simply appear again next time.
  }

  return next;
};

/**
 * When the dialog appeared, kept here only when the storage refused to save it (blocked or
 * full). Without it such a device would get the dialog on every reservation; with it, at most
 * once until the app reloads.
 */
let lastShownAtInMemory: string | null = null;

/** ISO timestamp of the last time the dialog appeared on this device, or null. */
export const readNotificationPromptLastShownAt = (): string | null => {
  try {
    const raw = localStorage.getItem(NOTIFICATION_PROMPT_SHOWN_STORAGE_KEY);
    if (raw && !Number.isNaN(Date.parse(raw))) {
      return raw;
    }
  } catch {
    // Storage blocked (private mode, cleared site data).
  }
  return lastShownAtInMemory;
};

/** Records that the dialog appeared on this device. */
export const recordNotificationPromptShown = (now: Date = new Date()): void => {
  try {
    localStorage.setItem(NOTIFICATION_PROMPT_SHOWN_STORAGE_KEY, now.toISOString());
  } catch {
    // Storage unavailable — remembered until the app reloads instead.
    lastShownAtInMemory = now.toISOString();
  }
};

/**
 * Forgets the "Не сега" history, e.g. once the user turns notifications on. The last time
 * the dialog appeared is kept, so it still stays away for its 30 days.
 */
export const clearNotificationPromptHistory = (
  storageKey: string = NOTIFICATION_PROMPT_STORAGE_KEY
): void => {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Storage unavailable — nothing to clear.
  }
};
